import type { FieldPath, FieldValues } from "react-hook-form";
import { Controller, useFormContext } from "react-hook-form";
import {
  AlgoliaAutocomplete,
  type AlgoliaAutocompleteOption,
  type AlgoliaAutocompleteProps,
} from "./autocomplete-algolia";

/**
 * React Hook Form bridge around {@link AlgoliaAutocomplete}: binds `selectedOption` /
 * `onSelectedOptionChange` to a field (`TItem | null`). Use inside `FormProvider`.
 *
 * For controlled search text without RHF, pass `inputValue` and `onInputChange` on
 * `AlgoliaAutocomplete` directly.
 *
 * Interaction coverage lives in `autocomplete-algolia.test.tsx` (same folder).
 */
export type ControlledAlgoliaAutocompleteProps<
  THit,
  TItem extends AlgoliaAutocompleteOption = AlgoliaAutocompleteOption,
> = Omit<
  AlgoliaAutocompleteProps<THit, TItem>,
  "onSelectedOptionChange" | "selectedOption"
> & {
  /**
   * `name` on the underlying Autocomplete input (tests / analytics). Defaults to the RHF field
   * path when omitted.
   */
  inputName?: string;
  name: string;
  /** Runs after RHF `field.onChange(option)` (e.g. mirror selection into sibling paths). */
  onAfterSelectedOptionChange?: (option: TItem | null) => void;
  /**
   * Extra RHF field paths whose validation errors should show on this control
   * (e.g. `payers.insurance.id` when the Controller `name` is `payers._insurance`).
   */
  relatedErrorFieldNames?: Array<FieldPath<FieldValues>>;
  /** Map stored form values to the option shown in the list (legacy / API shapes). */
  selectedOptionFromFieldValue?: (raw: unknown) => TItem | null;
};

export function ControlledAlgoliaAutocomplete<
  THit,
  TItem extends AlgoliaAutocompleteOption = AlgoliaAutocompleteOption,
>({
  inputName,
  name,
  onAfterSelectedOptionChange,
  relatedErrorFieldNames,
  selectedOptionFromFieldValue,
  ...props
}: ControlledAlgoliaAutocompleteProps<THit, TItem>) {
  const { control, formState, getFieldState } = useFormContext();

  return (
    <Controller
      control={control}
      name={name}
      render={({
        field: {
          name: fieldPath,
          onBlur,
          onChange,
          ref: _ref,
          value,
          ...restField
        },
        fieldState,
      }) => {
        const relatedErrorMessage = relatedErrorFieldNames
          ?.map((path) => getFieldState(path, formState).error?.message)
          .find((m): m is string => Boolean(m));

        const errorMessage =
          fieldState?.error?.message ?? relatedErrorMessage ?? undefined;
        const isInvalid = Boolean(errorMessage);

        const selectedOption =
          selectedOptionFromFieldValue != null
            ? selectedOptionFromFieldValue(value)
            : (value as TItem | null | undefined) ?? null;

        return (
          <AlgoliaAutocomplete<THit, TItem>
            {...props}
            {...restField}
            errorMessage={errorMessage}
            isInvalid={isInvalid}
            name={inputName ?? fieldPath}
            selectedOption={selectedOption}
            onBlur={onBlur}
            onSelectedOptionChange={(opt) => {
              onChange(opt);
              onAfterSelectedOptionChange?.(opt);
            }}
          />
        );
      }}
    />
  );
}

ControlledAlgoliaAutocomplete.displayName =
  "Recare.ControlledAlgoliaAutocomplete";
